import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  Building,
  Clock,
  ClipboardList,
  DollarSign,
  FileText,
  MapPin,
  ShieldCheck,
  Star,
  Stethoscope,
  AlertCircle,
} from "lucide-react";
import Loading from "@/components/Loading";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RenderHtml } from "@/components/RenderHtml";
import { ExternalJobBadge } from "../components/external-job-badge";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { getSalaryRange } from "../utils/get-salary-range";

export const JobPostDetailDialog = ({
  jobId,
  open,
  onOpenChange,
}: {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const trpc = useTRPC();
  const {
    data: job,
    isLoading,
    error,
  } = useQuery({
    ...trpc.jobPost.get.queryOptions({ id: jobId }),
    enabled: open,
  });

  const salaryRange = job ? getSalaryRange(job) : "";

  const location = useMemo(() => {
    if (!job) {
      return "";
    }
    if (job.city) {
      return job.stateCode ? `${job.city}, ${job.stateCode}` : job.city;
    }
    return job.stateCode || "";
  }, [job?.city, job?.stateCode]);

  const facilityName = useMemo(() => {
    if (!job) {
      return "";
    }
    return job?.facility?.name || job.facilityName || "";
  }, [job?.facility?.name, job?.facilityName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="[&.dialog-content]:max-w-[min(42rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden rounded-2xl border-slate-200 bg-white text-slate-900 sm:rounded-2xl">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="pr-8 text-xl font-semibold leading-snug tracking-tight">
            {isLoading ? "Loading..." : job?.title || "Job Post"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loading className="h-12 w-12" />
            </div>
          ) : error || !job ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <AlertCircle className="h-10 w-10 text-slate-400 mb-3" />
              <p className="text-sm text-slate-500">
                {error ? "Failed to load job post." : "Job post not found."}
              </p>
            </div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {salaryRange && (
                  <Badge
                    variant="outline"
                    className="bg-primary/5 border-primary/15 text-primary rounded-full px-3 py-0.5 text-xs font-medium"
                  >
                    <DollarSign className="h-3 w-3 mr-0.5" />
                    {salaryRange}
                  </Badge>
                )}
                {(job.durationIds ?? []).map((durationId) => (
                  <Badge
                    key={durationId}
                    variant="outline"
                    className="bg-slate-50 border-slate-200 text-slate-700 rounded-full px-3 py-0.5 text-xs font-medium"
                  >
                    {getJobDurationLabel(durationId)}
                  </Badge>
                ))}
                {job.facilityType && (
                  <Badge
                    variant="outline"
                    className="rounded-full px-3 py-0.5 text-xs font-medium bg-slate-50 border-slate-200 text-slate-600"
                  >
                    {job.facilityType.name}
                  </Badge>
                )}
                {job.externalUrl && <ExternalJobBadge />}
              </div>

              {/* Key Details */}
              <div className="space-y-2.5">
                {job.healthcareProfessionalTypes &&
                  job.healthcareProfessionalTypes.length > 0 && (
                    <div className="flex items-start gap-2.5">
                      <Stethoscope className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                      <div className="text-sm">
                        <span className="text-slate-500">
                          {job.healthcareProfessionalTypes
                            .map((t) => t.name)
                            .join(", ")}
                          :
                        </span>{" "}
                        <span className="font-medium text-slate-800">
                          {job.specialties && job.specialties.length > 0
                            ? job.specialties.map((s) => s.name).join(", ")
                            : "Not specified"}
                        </span>
                      </div>
                    </div>
                  )}
                {facilityName && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Building className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="text-slate-700">
                      {facilityName}
                      {job.facilityType ? ` (${job.facilityType.name})` : ""}
                    </span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="text-slate-700">{location}</span>
                  </div>
                )}
              </div>

              {/* Schedule Description */}
              {job.scheduleDescription && (
                <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-primary" />
                      Work Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                    <RenderHtml html={job.scheduleDescription} />
                  </CardContent>
                </Card>
              )}

              {/* Responsibilities */}
              {job.responsibilities && job.responsibilities.length > 0 && (
                <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <ClipboardList className="h-3.5 w-3.5 text-primary" />
                      Responsibilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4">
                    <ul className="space-y-2">
                      {job.responsibilities.map((item: string, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-slate-600"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Required Qualifications */}
              {job.requiredQualifications &&
                job.requiredQualifications.length > 0 && (
                  <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                        Required Qualifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-4">
                      <ul className="space-y-2">
                        {job.requiredQualifications.map(
                          (item: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-slate-600"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                              {item}
                            </li>
                          ),
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}

              {/* Preferred Qualifications */}
              {job.preferredQualifications &&
                job.preferredQualifications.length > 0 && (
                  <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Star className="h-3.5 w-3.5 text-primary" />
                        Preferred Qualifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-4">
                      <ul className="space-y-2">
                        {job.preferredQualifications.map(
                          (item: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-slate-600"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                              {item}
                            </li>
                          ),
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}

              {/* Description */}
              {job.description && (
                <Card className="shadow-sm border border-slate-200 bg-white rounded-2xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-primary" />
                      Job Description
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4 text-sm text-slate-600 prose prose-sm max-w-none">
                    <RenderHtml html={job.description} />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
